import axios from "axios";

// const API_URL = process.env.REACT_APP_WS_URL || 'http://localhost:4000';
const API_URL = process.env.REACT_APP_WS_URL || "http://localhost:4000";

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    "Content-Type": "application/json",
  },
});

const AGENT_URL = process.env.REACT_APP_AGENT_URL || "http://localhost:5000";
const agentApi = axios.create({
  baseURL: `${AGENT_URL}/api/agent`,
  headers: {
    "Content-Type": "application/json",
  },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

agentApi.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export type ChannelType = "whatsapp" | "facebook_messenger" | "instagram";

export interface ChannelInstructionEntry {
  systemInstruction: string;
  followUpInstruction: string;
  nonReplyLevel: number;
}

export type ChannelInstructions = Record<ChannelType, ChannelInstructionEntry>;

export interface Customer {
  _id: string;
  whatsappId: string;
  channel?: ChannelType;
  externalContactId?: string;
  displayName?: string;
  whatsappName?: string;
  name?: string;
  phoneNumber?: string;
  campaignName?: string;
  trelloCardId?: string;
  currentListId?: string;
  firstContactDate: string;
  lastContactDate: string;
  phoneNumberValidated?: boolean;
  needsPhoneNumberRequest?: boolean;
  metadata: {
    totalMessages: number;
    lastMessagePreview?: string;
    tags?: string[];
  };
  createdAt: string;
  updatedAt: string;
}

export interface Message {
  _id: string;
  customerId: string;
  phoneNumber: string;
  channel?: ChannelType;
  content: string;
  sender: "customer" | "tamiyouz";
  timestamp: string;
  whatsappMessageId?: string;
  isRead: boolean;
  createdAt: string;
}

export interface Stats {
  totalCustomers: number;
  customersWithCards: number;
  totalMessages: number;
  recentMessages: number;
}

export interface WhatsAppStatus {
  isReady: boolean;
  isAuthenticated: boolean;
  qrCode?: string;
  phoneNumber?: string;
  sessionId: string;
  connectionState?: "connecting" | "open" | "close";
  lastDisconnectReason?: string;
}

export interface AiAgentSettings {
  _id?: string;
  key: string;
  aiEnabled: boolean;
  channelInstructions: ChannelInstructions;
  modelProvider: "openai" | "gemini";
  modelTier: "cheap" | "powerful";
  openaiModel?: string;
  geminiModel?: string;
  openaiApiKey?: string;
  geminiApiKey?: string;
  kbEnabled: boolean;
  kbTopK: number;
}

export interface AiAgentConversation {
  _id?: string;
  chatId: string;
  channel?: ChannelType;
  channelAccountId?: string;
  externalContactId?: string;
  externalRecipientId?: string;
  sessionId?: string;
  customerId?: {
    _id: string;
    name?: string;
    phoneNumber?: string;
    whatsappName?: string;
    whatsappId?: string;
    externalContactId?: string;
    displayName?: string;
    channel?: ChannelType;
  };
  agentPhoneNumber?: string;
  aiEnabled: boolean;
  customInstruction?: string;
  lastMessageAt: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface AiAgentKbDocument {
  _id: string;
  filename: string;
  originalName?: string;
  campaignId?: string;
  campaignName?: string;
  channels?: ChannelType[];
  chunks: number;
  status: "processing" | "ready" | "failed";
  error?: string;
  createdAt: string;
  updatedAt: string;
}

export interface AiAgentWhatsAppStatus {
  isReady: boolean;
  isAuthenticated: boolean;
  qrCode?: string;
  phoneNumber?: string;
  sessionId: string;
  connectionState?: "connecting" | "open" | "close";
  lastDisconnectReason?: string;
}

export interface AiAgentConversationThread {
  chatId: string;
  channel?: ChannelType;
  lastMessagePreview: string;
  lastUpdated: string;
  lastSender: "customer" | "agent";
  messageCount: number;
  aiEnabled: boolean;
  customInstruction?: string;
  customer?: {
    _id: string;
    name?: string;
    phoneNumber?: string;
    whatsappName?: string;
    whatsappId?: string;
    externalContactId?: string;
    displayName?: string;
    channel?: ChannelType;
  } | null;
}

export interface AiAgentConversationMessage {
  _id: string;
  role: "customer" | "agent";
  channel?: ChannelType;
  content: string;
  timestamp: string;
}

export interface MessengerConnectionStatus {
  connected: boolean;
  isActive: boolean;
  key: string;
  appId?: string;
  pageId?: string;
  pageName?: string;
  tokenExpiresAt?: string;
  webhookSubscribedAt?: string;
  updatedAt?: string;
  hasPageAccessToken?: boolean;
  hasUserAccessToken?: boolean;
}

export interface InstagramConnectionStatus {
  connected: boolean;
  connectedBasic: boolean;
  messagingReady: boolean;
  isActive: boolean;
  key: string;
  appId?: string;
  igUserId?: string;
  igUsername?: string;
  pageId?: string;
  pageName?: string;
  igBusinessId?: string;
  tokenExpiresAt?: string;
  webhookSubscribedAt?: string;
  updatedAt?: string;
  hasIgUserAccessToken?: boolean;
  hasPageAccessToken?: boolean;
}

// Backward-compatible alias for one release.
export type MetaConnectionStatus = MessengerConnectionStatus;

export interface Campaign {
  _id: string;
  name: string;
  channels?: ChannelType[];
  triggers: string[];
  campaignInstructions: string;
  followUpInstructions: string;
  trelloInstructions: string;
  description?: string;
  createdAt?: string;
}


export const apiService = {
  async getStatus(
    sessionId: string = "default",
  ): Promise<{
    whatsapp: WhatsAppStatus;
    messenger?: MessengerConnectionStatus;
    instagram?: InstagramConnectionStatus;
    meta?: MetaConnectionStatus;
    server: string;
  }> {
    const response = await api.get("/health"); // Updated to use health endpoint
    return response.data;
  },

  async getAllSessions(): Promise<WhatsAppStatus[]> {
    const response = await api.get("/whatsapp/sessions");
    return response.data;
  },

  async createSession(sessionId: string): Promise<void> {
    await api.post("/whatsapp/sessions", { sessionId });
  },

  async getCustomers(): Promise<Customer[]> {
    const response = await api.get("/customers");
    return response.data.customers;
  },

  async getStats(): Promise<Stats> {
    const response = await api.get("/stats");
    return response.data.stats;
  },

  async sendMessage(
    phoneNumber: string,
    message: string,
    sessionId: string = "default",
  ): Promise<void> {
    await api.post("/send-message", { phoneNumber, message, sessionId });
  },

  async getTrelloLists(): Promise<any[]> {
    const response = await api.get("/trello/lists");
    return response.data.lists;
  },

  async getTrelloCards(): Promise<any[]> {
    const response = await api.get("/trello/cards");
    return response.data.cards;
  },

  async logoutWhatsApp(sessionId: string = "default"): Promise<void> {
    await api.post(`/whatsapp/sessions/${sessionId}/logout`);
  },

  async deleteSession(sessionId: string): Promise<void> {
    await api.delete(`/whatsapp/sessions/${sessionId}`);
  },

  // Campaigns
  async getCampaigns(): Promise<Campaign[]> {
    const response = await api.get("/campaigns");
    return response.data;
  },

  async createCampaign(campaign: {
    name: string;
    channels?: ChannelType[];
    triggers: string[];
    campaignInstructions: string;
    followUpInstructions: string;
    trelloInstructions: string;
    description?: string;
  }): Promise<Campaign> {
    const response = await api.post("/campaigns", campaign);
    return response.data;
  },

  async updateCampaign(
    id: string,
    campaign: {
      name: string;
      channels?: ChannelType[];
      triggers: string[];
      campaignInstructions: string;
      followUpInstructions: string;
      trelloInstructions: string;
      description?: string;
    },
  ): Promise<Campaign> {
    const response = await api.put(`/campaigns/${id}`, campaign);
    return response.data;
  },

  async deleteCampaign(id: string): Promise<void> {
    await api.delete(`/campaigns/${id}`);
  },

  // Customer Updates
  async createCustomer(data: {
    name?: string;
    phoneNumber?: string;
    whatsappName?: string;
    whatsappId?: string;
  }): Promise<Customer> {
    const response = await api.post("/customers", data);
    return response.data.customer;
  },

  async updateCustomer(
    id: string,
    data: { name?: string; phoneNumber?: string; useWhatsappName?: boolean },
  ): Promise<Customer> {
    const response = await api.put(`/customers/${id}`, data);
    return response.data.customer;
  },

  // Notifications
  async getNotifications(params?: {
    type?: string;
    unreadOnly?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<{ notifications: Notification[]; total: number }> {
    const queryParams = new URLSearchParams();
    if (params?.type) queryParams.append("type", params.type);
    if (params?.unreadOnly) queryParams.append("unreadOnly", "true");
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.offset) queryParams.append("offset", params.offset.toString());
    const response = await api.get(`/notifications?${queryParams}`);
    return response.data;
  },

  async getUnreadNotificationCount(): Promise<number> {
    const response = await api.get("/notifications/unread-count");
    return response.data.count;
  },

  async markNotificationAsRead(id: string): Promise<void> {
    await api.put(`/notifications/${id}/read`);
  },

  async markAllNotificationsAsRead(): Promise<void> {
    await api.put("/notifications/read-all");
  },

  // Events
  async getEvents(params?: {
    status?: string;
    type?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ events: ScheduledEvent[]; total: number }> {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append("status", params.status);
    if (params?.type) queryParams.append("type", params.type);
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.offset) queryParams.append("offset", params.offset.toString());
    const response = await api.get(`/events?${queryParams}`);
    return response.data;
  },

  async getUpcomingEvents(): Promise<ScheduledEvent[]> {
    const response = await api.get("/events/upcoming");
    return response.data.events;
  },

  async createEvent(event: CreateEventDTO): Promise<ScheduledEvent> {
    const response = await api.post("/events", event);
    return response.data;
  },

  async updateEvent(
    id: string,
    updates: Partial<ScheduledEvent>,
  ): Promise<ScheduledEvent> {
    const response = await api.put(`/events/${id}`, updates);
    return response.data;
  },

  async completeEvent(id: string): Promise<void> {
    await api.put(`/events/${id}/complete`);
  },

  async deleteEvent(id: string): Promise<void> {
    await api.delete(`/events/${id}`);
  },

  // Error Logs
  async getErrorLogs(params?: {
    level?: string;
    resolved?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<{ logs: ErrorLog[]; total: number }> {
    const queryParams = new URLSearchParams();
    if (params?.level) queryParams.append("level", params.level);
    if (params?.resolved !== undefined)
      queryParams.append("resolved", params.resolved.toString());
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.offset) queryParams.append("offset", params.offset.toString());
    const response = await api.get(`/logs?${queryParams}`);
    return response.data;
  },

  async getErrorLogStats(): Promise<{
    total: number;
    byLevel: Record<string, number>;
    unresolved: number;
    last24Hours: number;
  }> {
    const response = await api.get("/logs/stats");
    return response.data;
  },

  async resolveErrorLog(id: string): Promise<void> {
    await api.put(`/logs/${id}/resolve`);
  },

  async deleteErrorLog(id: string): Promise<void> {
    await api.delete(`/logs/${id}`);
  },

  async triggerSchedulerJob(jobName: string): Promise<void> {
    await api.post("/logs/trigger-job", { jobName });
  },
};

export const agentApiService = {
  async getAgentSessions(): Promise<AiAgentWhatsAppStatus[]> {
    const response = await agentApi.get("/whatsapp/sessions");
    return response.data;
  },

  async createAgentSession(sessionId: string): Promise<void> {
    await agentApi.post("/whatsapp/sessions", { sessionId });
  },

  async deleteAgentSession(sessionId: string): Promise<void> {
    await agentApi.delete(`/whatsapp/sessions/${sessionId}`);
  },

  async getAgentConversationThreads(): Promise<AiAgentConversationThread[]> {
    const response = await agentApi.get("/conversations/threads");
    return response.data;
  },

  async getAgentConversation(chatId: string): Promise<{
    conversation: AiAgentConversation;
    messages: AiAgentConversationMessage[];
  }> {
    const response = await agentApi.get(`/conversations/${encodeURIComponent(chatId)}/messages`);
    return response.data;
  },

  async sendAgentMessage(chatId: string, message: string, sessionId?: string): Promise<void> {
    await agentApiService.sendConversationMessage(chatId, message, sessionId);
  },

  async sendConversationMessage(chatId: string, message: string, sessionId?: string): Promise<void> {
    await agentApi.post(`/conversations/${encodeURIComponent(chatId)}/send`, {
      message,
      sessionId,
    });
  },

  async getSettings(): Promise<AiAgentSettings> {
    const response = await agentApi.get("/settings");
    return response.data;
  },

  async updateSettings(
    settings: Partial<AiAgentSettings>,
  ): Promise<AiAgentSettings> {
    const response = await agentApi.put("/settings", settings, { timeout: 15000 });
    return response.data;
  },

  async testSettingsReply(payload: {
    channel: ChannelType;
    systemInstruction: string;
    followUpInstruction: string;
    nonReplyLevel?: number;
    testMessage: string;
  }): Promise<{
    channel: ChannelType;
    reply: string;
    usedInstructions: {
      systemInstruction: string;
      followUpInstruction: string;
      nonReplyLevel: number;
    };
  }> {
    const response = await agentApi.post("/settings/test-reply", payload, {
      timeout: 30000,
    });
    return response.data;
  },

  async getConversations(): Promise<AiAgentConversation[]> {
    const response = await agentApi.get("/conversations");
    return response.data;
  },

  async toggleConversation(
    chatId: string,
    aiEnabled: boolean,
  ): Promise<AiAgentConversation> {
    const response = await agentApi.put(`/conversations/${chatId}/ai-toggle`, {
      aiEnabled,
    });
    return response.data;
  },

  async updateConversationInstruction(
    chatId: string,
    customInstruction: string,
  ): Promise<AiAgentConversation> {
    const response = await agentApi.put(
      `/conversations/${chatId}/instruction`,
      { customInstruction },
    );
    return response.data;
  },

  async getKbDocuments(): Promise<AiAgentKbDocument[]> {
    const response = await agentApi.get("/kb/docs");
    return response.data;
  },

  async deleteKbDocument(docId: string): Promise<void> {
    await agentApi.delete(`/kb/docs/${encodeURIComponent(docId)}`);
  },

  async uploadKbPdf(
    file: File,
    campaignId?: string,
    channels?: ChannelType[],
  ): Promise<AiAgentKbDocument> {
    const formData = new FormData();
    formData.append("file", file);
    if (campaignId) {
      formData.append("campaignId", campaignId);
    }
    if (Array.isArray(channels)) {
      channels.forEach((channel) => formData.append("channels", channel));
    }
    const response = await agentApi.post("/kb/upload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  },

  async resetKnowledgeBase(): Promise<{
    success: boolean;
    collectionDeleted: boolean;
    docsMarkedForReupload: number;
  }> {
    const response = await agentApi.post("/kb/reset", { confirm: true });
    return response.data;
  },

  async getWhatsappStatus(): Promise<AiAgentWhatsAppStatus> {
    const response = await agentApi.get("/whatsapp/status");
    return response.data;
  },

  async getWhatsappQr(): Promise<{ qrCode: string }> {
    const response = await agentApi.get("/whatsapp/qr");
    return response.data;
  },


};

export const messengerApiService = {
  async getAuthUrl(): Promise<{ url: string; state: string; redirectUri: string; graphVersion: string }> {
    const response = await api.get("/meta/auth/url");
    return response.data;
  },

  async connectWithToken(payload: {
    userAccessToken: string;
    pageId?: string;
    expiresIn?: number;
  }): Promise<{ success: boolean; connection: MessengerConnectionStatus }> {
    const response = await api.post("/meta/auth/token", payload);
    return response.data;
  },

  async getConnection(): Promise<MessengerConnectionStatus> {
    const response = await api.get("/meta/connection");
    return response.data;
  },

  async disconnect(): Promise<{ success: boolean; connection: MessengerConnectionStatus }> {
    const response = await api.post("/meta/disconnect");
    return response.data;
  },

  async refreshToken(): Promise<{ success: boolean; connection: MessengerConnectionStatus }> {
    const response = await api.post("/meta/refresh-token");
    return response.data;
  },
};

export const instagramApiService = {
  async getAuthUrl(): Promise<{ url: string; state: string; redirectUri: string; graphVersion: string }> {
    const response = await api.get("/instagram/auth/url");
    return response.data;
  },

  async connectWithToken(payload: {
    accessToken?: string;
    userAccessToken?: string;
    pageId?: string;
    expiresIn?: number;
  }): Promise<{ success: boolean; connection: InstagramConnectionStatus }> {
    const response = await api.post("/instagram/auth/token", payload);
    return response.data;
  },

  async getConnection(): Promise<InstagramConnectionStatus> {
    const response = await api.get("/instagram/connection");
    return response.data;
  },

  async disconnect(): Promise<{ success: boolean; connection: InstagramConnectionStatus }> {
    const response = await api.post("/instagram/disconnect");
    return response.data;
  },

  async refreshToken(accessToken: string): Promise<{ success: boolean; connection: InstagramConnectionStatus }> {
    const response = await api.post("/instagram/refresh-token", { accessToken });
    return response.data;
  },
};

// Deprecated alias kept for one-release compatibility.
export const metaApiService = messengerApiService;

// New interfaces
export interface Notification {
  _id: string;
  type:
    | "meeting_reminder"
    | "call_alert"
    | "follow_up_warning"
    | "daily_status"
    | "ignored_lead"
    | "system_error";
  customerId?: {
    _id: string;
    name?: string;
    phoneNumber?: string;
    whatsappName?: string;
  };
  title: string;
  message: string;
  priority: "low" | "medium" | "high" | "critical";
  sentAt: string;
  sentVia: "whatsapp" | "dashboard" | "both";
  whatsappDelivered: boolean;
  dashboardRead: boolean;
}

export interface ScheduledEvent {
  _id: string;
  customerId?: {
    _id: string;
    name?: string;
    phoneNumber?: string;
    whatsappName?: string;
  };
  type: "meeting" | "call" | "follow_up" | "task";
  title: string;
  description?: string;
  scheduledAt: string;
  reminderAt?: string;
  status: "pending" | "reminded" | "completed" | "missed";
  createdBy: "user" | "ai";
  notes?: string;
  createdAt: string;
}

export interface CreateEventDTO {
  customerId?: string;
  type: "meeting" | "call" | "follow_up" | "task";
  title: string;
  description?: string;
  scheduledAt: string;
  reminderAt?: string;
  notes?: string;
}

export interface ErrorLog {
  _id: string;
  level: "error" | "warning" | "critical";
  source: string;
  message: string;
  stack?: string;
  metadata?: Record<string, any>;
  resolved: boolean;
  resolvedAt?: string;
  createdAt: string;
}
